import {Badge, Box, Flex, Tooltip} from "@chakra-ui/react";
import {addMonths, format, getMonth, getYear} from "date-fns";
import React from "react";

interface SchedulerMonthsProps {
    startDate: Date;
    frequency: number;
    popover?: boolean;
    layout?: 'singleRow' | 'twoRows' | 'full'; // Новый параметр для выбора раскладки
}

const SchedulerMonths: React.FC<SchedulerMonthsProps> = ({
                                                             startDate,
                                                             frequency,
                                                             layout = "twoRows",
                                                             popover = true,
                                                         }) => {
    const startMonth = getMonth(startDate);
    const startYear = getYear(startDate);

    // Храним уникальные ключи "YYYY-MM"
    const scheduledMonthsSet = new Set<string>();
    let current = new Date(startDate);

    if (frequency > 0) {
        const monthInterval = Math.floor(frequency / 30);
        while (scheduledMonthsSet.size < 12) {
            const year = getYear(current);
            const month = getMonth(current);
            scheduledMonthsSet.add(`${year}-${month}`);
            current = addMonths(current, monthInterval);
        }
    }

    // Массив 12 месяцев, начиная с месяца старта
    const months = Array.from({length: 12}, (_, i) => {
        const date = addMonths(startDate, i);
        const year = getYear(date);
        const month = getMonth(date);
        return {
            name: format(date, "MMM"),
            fullName: format(date, "LLLL"),
            index: month,
            year,
        };
    });

    const chunkedMonths = (layout: string) => {
        if (layout === "twoRows") {
            return [months.slice(0, 6), months.slice(6, 12)];
        } else {
            return [months];
        }
    };

    const chunked = chunkedMonths(layout);

    return (
        <Flex direction="column" align="flex-start">
            {chunked.map((row, rowIndex) => (
                <Flex key={rowIndex} wrap="wrap" align="center">
                    {row.map(({name, fullName, index, year}) => {
                        const monthKey = `${year}-${index}`;
                        let color = "gray.500";
                        let tooltipLabel = "";

                        if (monthKey === `${startYear}-${startMonth}`) {
                            color = "blue.500";
                            tooltipLabel = `Next execution date: 1st ${fullName} ${year}`;
                        } else if (scheduledMonthsSet.has(monthKey)) {
                            color = "green.500";
                            tooltipLabel = `Will be executed on: 1st ${fullName} ${year}`;
                        }

                        const content = (
                            <Badge fontSize="sm" minW={10} cursor="pointer" color={color}
                                   textDecoration={tooltipLabel ? "underline" : "none"}>
                                {name}
                            </Badge>
                        );

                        return (
                            <Box
                                key={monthKey}
                                fontWeight="bold"
                                fontSize={popover ? 14 : 12}
                                p={popover ? "4px" : "2px"}
                            >
                                {tooltipLabel ? (
                                    <Tooltip label={tooltipLabel} hasArrow>
                                        {content}
                                    </Tooltip>
                                ) : (
                                    content
                                )}
                            </Box>
                        );
                    })}
                </Flex>
            ))}
        </Flex>
    );
};

export default SchedulerMonths;
